/**
 * ConciliationPanel
 *
 * Muestra el par REPO_DOC ↔ NAVIERA conciliado, permite desvincular
 * con confirmación inline, y tras desvincular muestra LinkBookingPanel
 * para re-vincular sin cerrar el modal padre.
 */
import { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Collapse,
  Chip,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  LinkOutlined,
  LinkOffOutlined,
  CheckCircleOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { unlinkBooking } from "@/services/reconciliation-api";
import { LinkBookingPanel } from "@/components/bookings/reconciliation/LinkBookingPanel";
import { ArchiveBookingDialog } from "@/components/bookings/archive-booking";
import { usePermissions } from "@/contexts/permissions-context";

export interface ConciliationPanelProps {
  repoDocBookingId: number;
  repoDocBookingReference: string | null;
  navieraBookingId: number;
  navieraBookingReference: string | null;
  mbl?: string | null;
  originPortId?: number | null;
  destinationPortId?: number | null;
  onUnlinked: () => void;
  onRelinked: (newNavieraId: number, newNavieraRef: string | null) => void;
}

export function ConciliationPanel({
  repoDocBookingId,
  repoDocBookingReference,
  navieraBookingId,
  navieraBookingReference,
  mbl,
  originPortId,
  destinationPortId,
  onUnlinked,
  onRelinked,
}: ConciliationPanelProps) {
  const { hasPermission } = usePermissions();
  const canWriteBookings = hasPermission("Bookings", "WRITE");
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);
  const [unlinked, setUnlinked] = useState(false);

  const handleUnlinkConfirm = useCallback(async () => {
    setUnlinking(true);
    setUnlinkError(null);
    try {
      await unlinkBooking({ bookingId: repoDocBookingId });
      setUnlinked(true);
      setConfirmOpen(false);
      onUnlinked();
    } catch (err) {
      setUnlinkError(err instanceof Error ? err.message : "Error al desvincular");
    } finally {
      setUnlinking(false);
    }
  }, [repoDocBookingId, onUnlinked]);

  if (unlinked) {
    return (
      <Box>
        <Alert severity="info" icon={<LinkOffOutlined />} sx={{ mb: 2 }}>
          Booking desvinculado. Puedes vincular a otro booking naviero:
        </Alert>
        <LinkBookingPanel
          bookingId={repoDocBookingId}
          bookingReference={repoDocBookingReference}
          bookingMbl={mbl}
          bookingOriginPortId={originPortId}
          bookingDestinationPortId={destinationPortId}
          onLinked={(newNavieraId, newNavieraRef) => onRelinked(newNavieraId, newNavieraRef)}
        />
      </Box>
    );
  }

  return (
    <Box>
      {/* Par conciliado */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
        {/* REPO_DOC */}
        <Paper
          variant="outlined"
          sx={{ p: 1.5, flex: 1, minWidth: 140, borderColor: "primary.200", bgcolor: "primary.50" }}
        >
          <Typography variant="caption" color="primary.main" fontWeight={700} sx={{ display: "block", mb: 0.5 }}>
            REPO DOC
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace" }}>
            {repoDocBookingReference ?? "Booking sin referencia"}
          </Typography>
        </Paper>

        {/* Icono de vínculo */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
          <LinkOutlined sx={{ fontSize: 28, color: "success.main" }} />
          <Chip
            icon={<CheckCircleOutlined />}
            label="Conciliado"
            size="small"
            color="success"
            sx={{ fontSize: "0.65rem", height: 20 }}
          />
        </Box>

        {/* NAVIERA */}
        <Paper
          variant="outlined"
          sx={{ p: 1.5, flex: 1, minWidth: 140, borderColor: "success.200", bgcolor: "success.50" }}
        >
          <Typography variant="caption" color="success.main" fontWeight={700} sx={{ display: "block", mb: 0.5 }}>
            NAVIERA
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace" }}>
            {navieraBookingReference ?? "Booking vinculado"}
          </Typography>
        </Paper>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {!confirmOpen && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<LinkOffOutlined />}
            onClick={() => setConfirmOpen(true)}
            sx={{ textTransform: "none" }}
          >
            Desvincular
          </Button>
          {canWriteBookings && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              aria-label={`Archivar ${repoDocBookingReference ?? `#${repoDocBookingId}`}`}
              onClick={() => setArchiveOpen(true)}
              sx={{ textTransform: "none" }}
            >
              Archivar
            </Button>
          )}
        </Box>
      )}

      <ArchiveBookingDialog
        open={archiveOpen}
        bookingId={repoDocBookingId}
        bookingReference={repoDocBookingReference ?? undefined}
        onClose={() => setArchiveOpen(false)}
        onCompleted={() => {
          setArchiveOpen(false);
          onUnlinked();
        }}
      />

      {/* Confirmación inline */}
      <Collapse in={confirmOpen}>
        <Alert
          severity="warning"
          icon={<WarningAmberOutlined />}
          sx={{ mt: 1 }}
          action={
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                size="small"
                color="inherit"
                onClick={() => { setConfirmOpen(false); setUnlinkError(null); }}
                disabled={unlinking}
                sx={{ textTransform: "none" }}
              >
                No
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={handleUnlinkConfirm}
                disabled={unlinking}
                startIcon={unlinking ? <CircularProgress size={12} /> : undefined}
                sx={{ textTransform: "none" }}
              >
                {unlinking ? "Desvinculando..." : "Sí, desvincular"}
              </Button>
            </Box>
          }
        >
          ¿Confirmar desvinculación? Esta acción no se puede deshacer.
        </Alert>
        {unlinkError && (
          <Alert severity="error" sx={{ mt: 1 }}>{unlinkError}</Alert>
        )}
      </Collapse>
    </Box>
  );
}
